/**
 * Voluum API Hooks
 * For Media Devoted | Created By Ciro Autiero
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { TrackerId } from '@trafficpulse/shared';
import api from '@/services/api';
import { fetchCampaignList } from '@/hooks/voluum/campaign-list';

// Types
export interface CampaignCountry {
  code: string;
  name: string;
}

/** The tracker a campaign row belongs to: routes the Campaigns page to /voluum, /binom or /tres-commas. */
export type CampaignProvider = TrackerId;

export interface Campaign {
  id: string;
  name: string;
  /** Present when backend normalizes Voluum campaign payloads. */
  flowId?: string;
  status?: string;
  country?: CampaignCountry | string;
  visits?: number;
  clicks?: number;
  conversions?: number;
  revenue?: number;
  cost?: number;
  profit?: number;
  /** Set by the campaign list hooks; lets the campaign page route to its tracker's routes. */
  provider?: CampaignProvider;
}

// Helper to get country display name
export function getCountryName(country?: CampaignCountry | string): string {
  if (!country) return 'Unknown';
  if (typeof country === 'string') return country;
  return country.name || country.code || 'Unknown';
}

export interface Flow {
  id: string;
  name: string;
  defaultPaths?: FlowPath[];
  pathGroups?: FlowPathGroup[];
}

export interface FlowPathGroup {
  id: string;
  name: string;
  active: boolean;
  smartRotation: boolean;
  paths: FlowPath[];
}

export interface FlowPath {
  id: string;
  name: string;
  weight: number;
  active?: boolean;
  landers?: FlowLander[];
  offers?: FlowOffer[];
}

export interface FlowLander {
  id: string;
  name: string;
  url: string;
  weight: number;
}

export interface FlowOffer {
  id: string;
  name: string;
  url: string;
  weight: number;
}

export interface ReportRow {
  [key: string]: string | number;
}

export interface CampaignBreakdownItem {
  id: string;
  name: string;
  visits?: number;
  clicks?: number;
  conversions?: number;
  revenue?: number;
  cost?: number;
  profit?: number;
  roi?: number;
}

export interface CampaignDetail extends Campaign {
  flows?: Flow[];
  trafficSources?: CampaignBreakdownItem[];
  gateways?: CampaignBreakdownItem[];
  leadsLists?: CampaignBreakdownItem[];
}

/** Section 8: optimization metrics per date range (pathMetrics, landerMetrics, offerMetrics by id). */
export interface OptimizationMetricsPerDateRange {
  pathMetrics: Record<string, Record<string, number>>;
  landerMetrics: Record<string, Record<string, number>>;
  offerMetrics: Record<string, Record<string, number>>;
}

export type OptimizationMetricsResponse = Record<string, OptimizationMetricsPerDateRange>;

// Query Keys
export const voluumKeys = {
  all: ['voluum'] as const,
  campaigns: () => [...voluumKeys.all, 'campaigns'] as const,
  campaign: (id: string) => [...voluumKeys.campaigns(), id] as const,
  campaignBreakdown: (id: string, type: string) => [...voluumKeys.campaign(id), 'breakdown', type] as const,
  campaignFlows: (id: string) => [...voluumKeys.campaign(id), 'flows'] as const,
  campaignTotals: (id: string, dateRange: string) => [...voluumKeys.campaign(id), 'totals', dateRange] as const,
  flows: () => [...voluumKeys.all, 'flows'] as const,
  flow: (id: string) => [...voluumKeys.flows(), id] as const,
  reports: (params: Record<string, unknown>) => [...voluumKeys.all, 'reports', params] as const,
  optimizationMetrics: (params: {
    flowId: string;
    dateRangePresets: string[];
    campaignId?: string;
    from?: string;
    to?: string;
  }) =>
    [
      ...voluumKeys.all,
      'reports',
      'optimization-metrics',
      params.flowId,
      params.dateRangePresets,
      params.campaignId ?? '',
      params.from ?? '',
      params.to ?? '',
    ] as const,
};

function tagVoluum<T extends { provider?: CampaignProvider }>(row: T): T {
  return row.provider ? row : { ...row, provider: 'voluum' };
}

// Hooks
export function useCampaigns() {
  return useQuery({
    queryKey: voluumKeys.campaigns(),
    queryFn: async () => (await fetchCampaignList<Campaign>('/voluum/campaigns')).map(tagVoluum),
    staleTime: 60000, // 1 minute
  });
}

export function useCampaign(campaignId: string) {
  return useQuery({
    queryKey: voluumKeys.campaign(campaignId),
    queryFn: async () => {
      const response = await api.get<{ data: CampaignDetail }>(`/voluum/campaigns/${campaignId}`);
      return response.data.data;
    },
    enabled: !!campaignId,
    staleTime: 60000,
  });
}

export function useCampaignFlows(campaignId: string) {
  return useQuery({
    queryKey: voluumKeys.campaignFlows(campaignId),
    queryFn: async () => {
      const response = await api.get<{ data: Flow[] }>(`/voluum/campaigns/${campaignId}/flows`);
      return response.data.data;
    },
    enabled: !!campaignId,
    staleTime: 60000,
  });
}

export function useFlows() {
  return useQuery({
    queryKey: voluumKeys.flows(),
    queryFn: async () => {
      const response = await api.get<{ data: Flow[] }>('/voluum/flows');
      return response.data.data;
    },
    staleTime: 60000,
  });
}

export function useFlow(flowId: string) {
  return useQuery({
    queryKey: voluumKeys.flow(flowId),
    queryFn: async () => {
      const response = await api.get<{ data: Flow }>(`/voluum/flows/${flowId}`);
      return response.data.data;
    },
    enabled: !!flowId,
    staleTime: 30000,
  });
}

export function useUpdateFlow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ flowId, updates }: { flowId: string; updates: Partial<Flow> }) => {
      const response = await api.put<{ data: Flow }>(`/voluum/flows/${flowId}`, updates);
      return response.data.data;
    },
    onSuccess: (data, { flowId }) => {
      queryClient.setQueryData(voluumKeys.flow(flowId), data);
      queryClient.invalidateQueries({ queryKey: voluumKeys.flows() });
    },
  });
}

export function useReports(params: {
  from?: string;
  to?: string;
  groupBy?: string;
  campaignId?: string;
}) {
  return useQuery({
    queryKey: voluumKeys.reports(params),
    queryFn: async () => {
      const response = await api.get<{ data: { rows: ReportRow[] } }>('/voluum/reports', {
        params,
      });
      return response.data.data.rows;
    },
    enabled: !!params.from && !!params.to,
    staleTime: 300000, // 5 minutes
  });
}

